import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Pencil, Trash2, Plus, ArrowLeft, Building2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

function SectorManager() {
  const navigate = useNavigate();
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [newSector, setNewSector] = useState({
    name: '',
    description: '',
    ideal_profile: { d: 0.25, i: 0.25, s: 0.25, c: 0.25 }
  });

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sectors');
      setSectors(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Erro ao carregar setores');
      console.error(error);
      setSectors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSector = async () => {
    if (!newSector.name.trim()) {
      toast.error('Nome do setor é obrigatório');
      return;
    }

    // Validate sum of ideal profile
    const sum = Object.values(newSector.ideal_profile).reduce((a, b) => a + b, 0);
    if (Math.abs(sum - 1.0) > 0.01) {
      toast.error('A soma dos pesos DISC deve ser 100%');
      return;
    }

    try {
      await api.post('/sectors', newSector);
      toast.success('Setor criado com sucesso!');
      setOpenCreate(false);
      setNewSector({ name: '', description: '', ideal_profile: { d: 0.25, i: 0.25, s: 0.25, c: 0.25 } });
      fetchSectors();
    } catch (error) {
      toast.error('Erro ao criar setor');
      console.error(error);
    }
  };

  const handleUpdateSector = async () => {
    if (!editingSector.name.trim()) {
      toast.error('Nome do setor é obrigatório');
      return;
    }

    const sum = Object.values(editingSector.ideal_profile).reduce((a, b) => a + b, 0);
    if (Math.abs(sum - 1.0) > 0.01) {
      toast.error('A soma dos pesos DISC deve ser 100%');
      return;
    }

    try {
      await api.patch(`/sectors/${editingSector.id}`, editingSector);
      toast.success('Setor atualizado com sucesso!');
      setOpenEdit(false);
      setEditingSector(null);
      fetchSectors();
    } catch (error) {
      toast.error('Erro ao atualizar setor');
      console.error(error);
    }
  };

  const handleDeleteSector = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este setor?')) {
      return;
    }

    try {
      await api.delete(`/sectors/${id}`);
      toast.success('Setor excluído com sucesso!');
      fetchSectors();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Não é possível excluir este setor');
      } else {
        toast.error('Erro ao excluir setor');
      }
      console.error(error);
    }
  };

  const handleSliderChange = (factor, value, isEditing = false) => {
    const newValue = value[0] / 100;
    if (isEditing) {
      setEditingSector({
        ...editingSector,
        ideal_profile: { ...editingSector.ideal_profile, [factor]: newValue }
      });
    } else {
      setNewSector({
        ...newSector,
        ideal_profile: { ...newSector.ideal_profile, [factor]: newValue }
      });
    }
  };

  const renderProfileSliders = (profile, isEditing = false) => {
    const factors = [
      { 
        key: 'd', 
        label: 'Dominância (D)', 
        color: 'text-red-600',
        description: 'Direto, assertivo, orientado a resultados. Toma decisões rápidas e gosta de desafios.'
      },
      { 
        key: 'i', 
        label: 'Influência (I)', 
        color: 'text-yellow-600',
        description: 'Comunicativo, entusiasta, otimista. Gosta de interagir e motivar pessoas.'
      },
      { 
        key: 's', 
        label: 'Estabilidade (S)', 
        color: 'text-green-600',
        description: 'Paciente, leal, constante. Prefere ambientes previsíveis e trabalho em equipe.'
      },
      { 
        key: 'c', 
        label: 'Conformidade (C)', 
        color: 'text-blue-600',
        description: 'Analítico, preciso, metódico. Valoriza qualidade, dados e procedimentos.'
      }
    ];

    const sum = Object.values(profile).reduce((a, b) => a + b, 0);

    return (
      <div className="space-y-4">
        {factors.map(({ key, label, color, description }) => (
          <div key={key} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <Label className={`${color} font-semibold`}>{label}</Label>
              <span className="text-sm font-bold">{Math.round(profile[key] * 100)}%</span>
            </div>
            <p className="text-xs text-gray-600 mb-3 italic">{description}</p>
            <Slider
              value={[profile[key] * 100]}
              onValueChange={(val) => handleSliderChange(key, val, isEditing)}
              max={100}
              step={1}
              className="w-full"
            />
          </div>
        ))}
        <div className="pt-2 border-t">
          <div className="flex justify-between items-center">
            <span className="font-semibold">Total:</span>
            <span className={`font-bold ${Math.abs(sum - 1.0) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
              {Math.round(sum * 100)}%
            </span>
          </div>
          {Math.abs(sum - 1.0) >= 0.01 && (
            <p className="text-xs text-red-600 mt-1">⚠️ O total deve ser 100%</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gerenciar Setores</h1>
              <p className="text-gray-600">Defina setores da empresa e seus perfis DISC ideais</p>
            </div>
          </div>

          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="mr-2 h-4 w-4" />
                Novo Setor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Criar Novo Setor</DialogTitle>
                <DialogDescription>
                  Defina o nome, descrição e perfil DISC ideal para este setor
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="name">Nome do Setor *</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Suporte N1, Vendas, Qualidade"
                    value={newSector.name}
                    onChange={(e) => setNewSector({ ...newSector, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    placeholder="Descreva as responsabilidades deste setor..."
                    value={newSector.description}
                    onChange={(e) => setNewSector({ ...newSector, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Perfil DISC Ideal</Label>
                  <p className="text-sm text-gray-500 mb-3">
                    Ajuste os pesos de cada fator DISC para definir o perfil ideal (total deve ser 100%)
                  </p>
                  {renderProfileSliders(newSector.ideal_profile)}
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setOpenCreate(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateSector} className="bg-emerald-600 hover:bg-emerald-700">
                    Criar Setor
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Sectors List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Carregando...</p>
          </div>
        ) : sectors.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">Nenhum setor criado ainda</p>
              <Button onClick={() => setOpenCreate(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="mr-2 h-4 w-4" />
                Criar Primeiro Setor
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {sectors.map((sector) => (
              <Card key={sector.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        {sector.name}
                      </CardTitle>
                      <CardDescription className="mt-2">
                        {sector.description || 'Sem descrição'}
                      </CardDescription>
                      <div className="mt-4 grid grid-cols-4 gap-3">
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Dominância</div>
                          <div className="text-lg font-bold text-red-600">
                            {Math.round(sector.ideal_profile.d * 100)}%
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Influência</div>
                          <div className="text-lg font-bold text-yellow-600">
                            {Math.round(sector.ideal_profile.i * 100)}%
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Estabilidade</div>
                          <div className="text-lg font-bold text-green-600">
                            {Math.round(sector.ideal_profile.s * 100)}%
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Conformidade</div>
                          <div className="text-lg font-bold text-blue-600">
                            {Math.round(sector.ideal_profile.c * 100)}%
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingSector(sector);
                          setOpenEdit(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteSector(sector.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        {editingSector && (
          <Dialog open={openEdit} onOpenChange={setOpenEdit}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Editar Setor</DialogTitle>
                <DialogDescription>
                  Atualize as informações e perfil DISC ideal do setor
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="edit-name">Nome do Setor *</Label>
                  <Input
                    id="edit-name"
                    value={editingSector.name}
                    onChange={(e) => setEditingSector({ ...editingSector, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-description">Descrição</Label>
                  <Textarea
                    id="edit-description"
                    value={editingSector.description || ''}
                    onChange={(e) => setEditingSector({ ...editingSector, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Perfil DISC Ideal</Label>
                  <p className="text-sm text-gray-500 mb-3">
                    Ajuste os pesos de cada fator DISC (total deve ser 100%)
                  </p>
                  {renderProfileSliders(editingSector.ideal_profile, true)}
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => {
                    setOpenEdit(false);
                    setEditingSector(null);
                  }}>
                    Cancelar
                  </Button>
                  <Button onClick={handleUpdateSector} className="bg-emerald-600 hover:bg-emerald-700">
                    Atualizar Setor
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

export default SectorManager;
